import React from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from './useDocumentMeta';
import { BOOKING_URL, DELIVERY_PLATFORMS, HEAD_BITCH_QUOTE, RELEASE_QUALIFIER } from './siteConfig';
import './HomePage.css';

const HomePage: React.FC = () => {
  useDocumentMeta(
    'Cadence IO: every release, everywhere it needs to go',
    'Cadence holds your release marketing information, delivers it to each DSP in the format that one asks for, and tracks performance after the release is out.'
  );

  return (
    <div className="homepage">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-dots" />
        </div>
        <div className="hero-top-spacer" />
        <div className="hero-content">
          <h1>
            <span className="headline-line">Every release, everywhere it needs to go.</span>
          </h1>
          <p className="hero-subhead">
            Your release information is entered once, then delivered in the format every destination asks for.
          </p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Book a call
          </a>
        </div>
        <div className="hero-bottom-spacer" />
        <div className="hero-pillars">
          <p>One entry, every destination</p>
          <p>Your spreadsheet, connected</p>
          <p>Built by an operator</p>
        </div>
      </section>

      {/* The problem */}
      <section className="problem">
        <div className="problem-content">
          <p className="eyebrow">The problem</p>
          <div className="problem-row">
            <p className="problem-number">88%</p>
            <div className="problem-text">
              <p className="problem-evidence">
                of songs on streaming got fewer than 1,000 plays in 2025. More than half of those got fewer than 10 (<a href="https://luminatedata.com/reports/midyear-music-industry-report-2025/" target="_blank" rel="noopener noreferrer" className="inline-link">Luminate</a>).
              </p>
            </div>
          </div>
          <p className="problem-claim">Come the next release, there's no record of what was sent, who it went to, or whether it worked.</p>
        </div>
      </section>

      {/* How Cadence works */}
      <section className="blocks">
        <p className="eyebrow">How Cadence works</p>

        <div className="block">
          <div className="block-text">
            <h3>Out to the platforms</h3>
            <p className="block-fact">One entry, delivered to each platform in the format that one asks for.</p>
            <ul className="platform-list">
              {DELIVERY_PLATFORMS.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
            <p className="platform-conditions">Everything is submitted through your own access to each platform. Apple Music where your company has pitch access. SiriusXM and Pandora for Canadian artists.</p>
            <p className="block-line">The part that used to be your Tuesday afternoon.</p>
          </div>
          <div className="block-image">
            <img src={require('./Images/hero-pitch-form-crop.png')} alt="A release with all its delivery destinations selected in Cadence" />
          </div>
        </div>

        <div className="block block-reverse">
          <div className="block-text">
            <h3>Out to the people</h3>
            <p className="block-fact">Your contacts sit on the release, and the notifications that go with it are sent from the same place.</p>
            <p className="block-line">The relationships that go cold because nobody had time to send the email.</p>
          </div>
          <div className="block-image">
            <img src={require('./Images/block-2-contacts.png')} alt="Contacts attached to a release in Cadence" />
          </div>
        </div>

        <div className="block">
          <div className="block-text">
            <h3>In from your systems</h3>
            <p className="block-fact">Connect the Google Sheet or Airtable your release information already lives in and it flows straight into Cadence. Artists carry their platform IDs with them.</p>
            <p className="block-line">You type an artist's name once. Every release after that starts pre-filled.</p>
          </div>
          <div className="block-image">
            <img src={require('./Images/block-3-roster.png')} alt="A roster synced from a spreadsheet in Cadence" />
          </div>
        </div>

        <div className="block block-reverse">
          <div className="block-text">
            <h3>Back as performance</h3>
            <p className="block-fact">After release, playlist placements, popularity scores, and radio spins attach to the release they came from, tracked across Spotify, Apple Music, Amazon Music, Deezer, Tidal, SoundCloud, Audiomack, and YouTube.</p>
            <p className="block-line">The record of a release usually stops the day it comes out.</p>
          </div>
          <div className="block-image">
            <img src={require('./Images/block-4-performance.png')} alt="Performance data attached to a release in Cadence" />
          </div>
        </div>
      </section>

      {/* In use */}
      <section className="proof">
        <div className="proof-content">
          <p className="eyebrow">In use</p>
          <h2 className="proof-heading">From the teams running on it.</h2>
          <p className="proof-qualifier">{RELEASE_QUALIFIER}</p>

          <div className="proof-columns">
            <blockquote className="proof-quote">
              <p>{HEAD_BITCH_QUOTE.text}</p>
              <cite>{HEAD_BITCH_QUOTE.attribution}</cite>
            </blockquote>

            <blockquote className="proof-quote">
              <p>"It's a no-brainer that cuts down work time tremendously when pitching to DSPs."</p>
              <cite>Preach Records</cite>
              <Link to="/case-study" className="proof-link">How Preach Records uses it</Link>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Close */}
      <section className="close">
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
        >
          Book a call
        </a>
      </section>
    </div>
  );
};

export default HomePage;
